/**
 * collabMerge.ts
 * 3-way merge at id/key level for collaborative editing with optimistic locking.
 *
 * On a version conflict (another user wrote first), the client's own save must
 * not silently overwrite the other user's work. Instead, we merge:
 *   - base   = the last successfully synced state of this client
 *   - local  = the current (not-yet-synced) local state
 *   - remote = the fresh state from the database (with the other user's changes)
 *
 * Use mergeById/mergeByKey for any list where two clients could plausibly add
 * DIFFERENT items concurrently (boxes, arrows, resources, departments, areas,
 * mindmap nodes, ...) — mergeScalar treats a list as one opaque value and picks
 * local-or-remote wholesale, which silently drops the other side's additions
 * whenever the list itself changed. This is exactly what happened to a
 * flowchart's `resources` list in production: two collaborators' tabs each had
 * a different resources array, mergeScalar picked one side's whole array over
 * the other's, and the loser's entries were gone with no server-side history to
 * recover them from (flowchart_versions never snapshotted `resources` either —
 * see the version-restore code, which now does). mergeScalar remains correct
 * for genuinely atomic single values (title, a single toggle), never for lists.
 *
 * Rules per item (box/arrow/node/resource/...), compared via JSON serialization:
 *   - unchanged locally vs. base  → remote wins (including a remote deletion)
 *   - changed locally vs. base    → local wins (even if remote also changed
 *     or deleted it — a change on both sides is a genuine conflict; we
 *     consistently pick the local change, and the other side keeps theirs via
 *     the same rule on their end plus the realtime updates)
 *   - only new locally             → add it
 *   - only new remotely            → add it
 *
 * Known limitation: id collisions between items added concurrently (both
 * clients used the same nextId). The local variant wins in that case; the
 * remote addition is lost. This was also true under the old last-write-wins
 * model; the real fix is uuid ids, which is out of scope for this step.
 */

interface HasId {
  id: number;
}

// Shared by mergeByKey's own wipeout guard below AND guardWholesaleWipeout —
// nothing in this app's UI lets a user bulk-clear more than a handful of
// boxes/arrows/resources in one action, so a list this size collapsing to
// completely empty is far more likely to be one side's state being
// accidentally empty than genuine intent. See mergeByKey's own comment for
// the production incident this was found from.
export const SUSPICIOUS_WIPEOUT_THRESHOLD = 10;

/**
 * Same per-item 3-way merge as mergeById, keyed by an arbitrary caller-supplied
 * key instead of a numeric `id` field — for lists like Resource/Department that
 * are identified by `name` rather than a numeric id. mergeById is a thin wrapper
 * around this for the numeric-id case.
 */
export function mergeByKey<T>(base: T[], local: T[], remote: T[], keyFn: (item: T) => string | number): T[] {
  const baseJson = new Map<string | number, string>(base.map(i => [keyFn(i), JSON.stringify(i)]));
  const localMap = new Map<string | number, T>(local.map(i => [keyFn(i), i]));
  const result: T[] = [];
  const seen = new Set<string | number>();

  // Remote order as the starting point (that's the new shared baseline),
  // with local changes layered on top.
  for (const r of remote) {
    const k = keyFn(r);
    seen.add(k);
    const l = localMap.get(k);
    const b = baseJson.get(k);
    if (!l) {
      if (b !== undefined && JSON.stringify(r) === b) continue; // deleted locally, unchanged remotely → honor the deletion
      result.push(r); // new remotely, or changed remotely while deleted locally → keep remote
    } else {
      const localChanged = b === undefined || JSON.stringify(l) !== b;
      result.push(localChanged ? l : r);
    }
  }

  // Local items remote doesn't (yet) have: added locally, or deleted remotely.
  for (const l of local) {
    const k = keyFn(l);
    if (seen.has(k)) continue;
    const b = baseJson.get(k);
    if (b === undefined || JSON.stringify(l) !== b) {
      result.push(l); // new locally, or changed locally while deleted remotely → keep local
    }
    // otherwise: unchanged locally and deleted remotely → honor the deletion
  }

  // Safety net: a per-item merge that wipes out a LARGE base list down to
  // completely empty is far more often a sign that one side's in-memory state
  // was ACCIDENTALLY empty (a load/ref-desync race, a version restore that
  // forgot to update the sync baseline, a dropped realtime event, ...) than a
  // genuine "the user deleted every single item" action — confirmed in
  // production for exactly this: a flowchart's 196 arrows were wiped to `[]` in
  // one merge because one client's own in-memory `arrows` was empty at the
  // moment a conflict merge ran, and every arrow (unchanged vs. base on the
  // remote side) got honored above as "deleted locally". Deliberately scoped to
  // large lists only (threshold below) — deleting the handful of items in a
  // small list via a real multi-select is unremarkable and must still work
  // (see the "honors a local deletion" tests), but nothing in this app lets a
  // user bulk-clear dozens of boxes/arrows/resources in one action, so a wipe
  // past that size is safe to treat as corruption rather than intent.
  if (result.length === 0 && base.length > SUSPICIOUS_WIPEOUT_THRESHOLD) {
    return remote.length > 0 ? remote : local;
  }

  return result;
}

export function mergeById<T extends HasId>(base: T[], local: T[], remote: T[]): T[] {
  return mergeByKey(base, local, remote, item => item.id);
}

/**
 * Cheap, O(1) counterpart to mergeByKey/mergeById's own wipeout guard above —
 * for the "no local divergence, just adopt the incoming side wholesale" fast
 * paths (useFlowchartSync.ts's onUpdate skipMerge branch) that deliberately
 * skip the full per-item diff for performance. Without this, a corrupted
 * remote row (e.g. from some OTHER bug, or a stray direct DB edit) with a
 * large list gone to `[]` would get adopted with no protection at all, even
 * though the full merge path right next to it is now guarded against exactly
 * this. `current` is this client's own already-synced value (safe to fall
 * back to, since skipMerge means it hasn't diverged from base anyway).
 */
export function guardWholesaleWipeout<T>(current: T[], incoming: T[]): T[] {
  if (incoming.length === 0 && current.length > SUSPICIOUS_WIPEOUT_THRESHOLD) return current;
  return incoming;
}

/**
 * Scalar merge for standalone fields (title, the departments array as a whole, etc.):
 * local wins if it differs from base, otherwise remote.
 */
export function mergeScalar<T>(base: T, local: T, remote: T): T {
  return JSON.stringify(local) !== JSON.stringify(base) ? local : remote;
}
